import React, { useMemo } from 'react';
import clsx from 'clsx';
import Select, { SingleValue } from 'react-select';
import { useCredit } from '../../providers/CreditStatusProvider';
import styles from '../../styles/common/UserCreditLinesDropDownMenu.module.scss';

interface UserCreditLinesDropDownMenuProps {
  onSelect?: (creditLineId: number, creditLineName?: string) => void;
  selectedCreditLineId?: number | null;
  className?: string;
}

interface OptionType {
  value: number;
  label: string;
}

function UserCreditLinesDropDownMenu({ 
  onSelect, 
  selectedCreditLineId = null,
  className
}: UserCreditLinesDropDownMenuProps) {
  const { allCreditSummaries, isLoading, error } = useCredit();
  
  // Create credit lines from all credit summaries
  const creditLines = useMemo(() => {
    if (allCreditSummaries && allCreditSummaries.length > 0) {
      // Create entries for each credit summary
      const dynamicLines = allCreditSummaries.reduce((acc, summary, index) => {
        acc[`Credit Line ${index + 1}`] = index;
        return acc;
      }, {} as Record<string, number>);
      
      return dynamicLines;
    }
    
    // Default fallback - show at least one credit line when loading
    return {
      'Credit Line 1': 0
    };
  }, [allCreditSummaries]);

  // Convert credit lines to react-select options
  const options: OptionType[] = useMemo(() => {
    return Object.entries(creditLines).map(([name, id]) => ({
      value: id,
      label: name
    }));
  }, [creditLines]);

  // Find the selected option
  const selectedOption = selectedCreditLineId !== null 
    ? options.find(option => option.value === selectedCreditLineId) || options[0]
    : options[0];

  const handleChange = (selectedOption: SingleValue<OptionType>) => {
    if (selectedOption) {
      onSelect?.(selectedOption.value, selectedOption.label);
    }
  };

  // Custom styles for react-select to match the app's design
  const customStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      minHeight: 'auto',
      height: '40px',
      border: state.isFocused ? '2px solid #0070f3' : '1px solid #e1e5e9',
      borderRadius: '8px',
      boxShadow: state.isFocused ? '0 0 0 1px #0070f3' : 'none',
      backgroundColor: '#ffffff',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      '&:hover': {
        borderColor: state.isFocused ? '#0070f3' : '#b0b8c1'
      }
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      height: '38px',
      padding: '0 12px'
    }),
    input: (provided: any) => ({
      ...provided,
      margin: '0px'
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    indicatorsContainer: (provided: any) => ({
      ...provided,
      height: '38px'
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      color: '#6b7280',
      padding: '8px'
    }),
    menu: (provided: any) => ({
      ...provided,
      borderRadius: '8px',
      border: '1px solid #e1e5e9',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      zIndex: 9999
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected 
        ? '#0070f3' 
        : state.isFocused 
          ? '#f8fafc' 
          : 'white',
      color: state.isSelected ? 'white' : '#374151',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: state.isSelected ? '500' : '400',
      '&:active': {
        backgroundColor: state.isSelected ? '#0070f3' : '#e5f3ff'
      }
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: '#374151',
      fontSize: '14px',
      fontWeight: '500'
    })
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className={clsx(styles.cCreditLinesDropdown, "c-credit-lines-dropdown", className)}>
        <div className="c-credit-lines-dropdown-loading">
          Loading credit lines...
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className={clsx(styles.cCreditLinesDropdown, "c-credit-lines-dropdown", className)}>
        <div className="c-credit-lines-dropdown-error">
          Error loading credit lines
        </div>
      </div>
    );
  }

  return (
    <div className={clsx(styles.cCreditLinesDropdown, "c-credit-lines-dropdown", className)}>
      <Select
        value={selectedOption}
        onChange={handleChange}
        options={options}
        styles={customStyles}
        isSearchable={false}
        placeholder="Select credit line"
        classNamePrefix="react-select"
      />
    </div>
  );
}

export default UserCreditLinesDropDownMenu;